/**
 * Account lifecycle — sessions, linked providers, export, deletion.
 *
 * Every destructive action here has a server-side guard that the client cannot
 * satisfy by asking nicely. The disabled buttons below are hints; the refusals
 * are real.
 */
import type { ProviderId } from "../../shared/account";
import type { Tier } from "../../shared/tiers";
import { ApiError, request } from "./client";

/**
 * These mirror the Worker's own types exactly.
 *
 * Every one of them was wrong when first written — invented from memory of the
 * endpoints rather than read off them — and TypeScript cannot see across an
 * HTTP boundary, so nothing complained. `worker/contract.test.ts` now fetches
 * each route and asserts the key sets match, because that is the only place the
 * mismatch is observable.
 */
export interface SessionSummary {
  id: string;
  device: string;
  /** Coarse network, never the exact address — truncated to /24 or /48. */
  network: string | null;
  createdAt: number;
  expiresAt: number;
  current: boolean;
}

export interface LinkedProvider {
  provider: ProviderId;
  linkedAt: number;
}

export interface AccountSummary {
  providers: LinkedProvider[];
  projectCount: number;
  sessionCount: number;
  storageBytes: number;
  tier: Tier;
}

/**
 * The keys above, at runtime, kept exhaustive by the compiler.
 *
 * `satisfies Record<keyof T, true>` is the whole trick: omit a key and it is a
 * type error, add one that is not on the interface and it is also a type error.
 * `worker/contract.test.ts` asserts the live response matches this list, so the
 * type, the runtime list and the actual JSON are pinned to each other. Drift on
 * either side of the boundary now fails a test instead of reaching a browser.
 */
export const ACCOUNT_SUMMARY_KEYS = Object.keys({
  providers: true,
  projectCount: true,
  sessionCount: true,
  storageBytes: true,
  tier: true,
} satisfies Record<keyof AccountSummary, true>);

export const SESSION_SUMMARY_KEYS = Object.keys({
  id: true,
  device: true,
  network: true,
  createdAt: true,
  expiresAt: true,
  current: true,
} satisfies Record<keyof SessionSummary, true>);

export const LINKED_PROVIDER_KEYS = Object.keys({
  provider: true,
  linkedAt: true,
} satisfies Record<keyof LinkedProvider, true>);

export async function getAccount(signal?: AbortSignal): Promise<AccountSummary> {
  return request("/api/account", { signal });
}

export async function listSessions(signal?: AbortSignal): Promise<SessionSummary[]> {
  const { sessions } = await request<{ sessions: SessionSummary[] }>("/api/account/sessions", {
    signal,
  });
  return sessions;
}

export async function revokeSession(id: string): Promise<void> {
  await request(`/api/account/sessions/${encodeURIComponent(id)}`, { method: "DELETE" });
}

/**
 * Sign out everywhere else.
 *
 * Keeps this device by default. Signing someone out of the machine they are
 * actively using to secure their account is hostile, and it is the exact moment
 * they are most likely to panic.
 */
export async function revokeAllSessions(
  includeCurrent = false,
): Promise<{ deleted: number; revokedBefore: number }> {
  return request(`/api/account/sessions/revoke-all${includeCurrent ? "?includeCurrent=1" : ""}`, {
    method: "POST",
  });
}

/**
 * Unlink a sign-in provider.
 *
 * Refused server-side if it is the last one — an account with no way in is not
 * a security improvement, it is a lockout.
 */
export async function unlinkProvider(
  provider: ProviderId,
): Promise<{ ok: boolean; providers: ProviderId[] }> {
  // Server returns the remaining provider ids, having already refused if this
  // would be the last one.
  return request(`/api/account/providers/${encodeURIComponent(provider)}`, { method: "DELETE" });
}

/**
 * Download everything, as a zip.
 *
 * A plain navigation rather than fetch-then-blob: the response is streamed and
 * can be large, and buffering an entire account into memory to hand it to the
 * same browser that could have streamed it straight to disk is pointless.
 *
 * Deliberately offered *before* deletion in the UI. Someone deleting an account
 * should not discover afterwards that export existed.
 */
export function exportAccountUrl(): string {
  return "/api/account/export";
}

/**
 * Request deletion.
 *
 * Requires a *recent* sign-in, not merely a valid session — sessions last 30
 * days, so "signed in" is no evidence about who is at the keyboard. A 403 with
 * `reauth_required` means send them back through sign-in first.
 */
export async function requestDeletion(): Promise<{ ok: boolean; stage: DeletionStage }> {
  return request("/api/account", { method: "DELETE" });
}

/**
 * How far deletion got before the response was sent.
 *
 * Anything short of "done" is not a failure the user can act on — the cron
 * sweep finishes it — so the UI reports success either way and says the work is
 * already gone, because from their side it is.
 */
export type DeletionStage = "pending" | "external" | "blobs" | "rows" | "done";

export function needsReauth(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403 && err.code === "reauth_required";
}

export function explainAccountFailure(err: unknown): string {
  if (!(err instanceof ApiError)) return "That did not work. Nothing changed.";
  if (err.isOffline) return "You are offline. Nothing changed.";
  if (needsReauth(err)) return "Sign in again to confirm it is you.";
  if (err.code === "cannot_unlink") return err.message;
  if (err.isRateLimited) {
    return err.retryAfter
      ? `Too many attempts. Try again in ${err.retryAfter}s.`
      : "Too many attempts.";
  }
  if (err.isUnauthenticated) return "You are signed out.";
  return "That did not work. Nothing changed.";
}
